import type { RequestHistoryFilters } from "./request-history";
import type { MediaAsset } from "./assets";
import type { CursorPage, JsonObject, JsonValue } from "./json";
import type { NexraSettlement } from "./catalog";
import { pickPublicErrorFields, type NexraError, type PublicError, type PublicErrorContext, type PublicErrorFields } from "./errors";

export const TASK_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelling",
  "cancelled",
] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

export type TerminalTaskStatus = "succeeded" | "failed" | "cancelled";

export interface TaskProgress {
  percent?: number;
  stage?: string;
  message?: string;
}

export type TaskError = NexraError;

export interface TaskUsage {
  components: Array<{
    name: string;
    quantity: number;
    unit: string;
  }>;
  metadata?: JsonObject;
}

export interface TaskCost {
  amount: string;
  currency: string;
}

export type TaskBillingReason = "request_failed" | "request_cancelled"
  | "billing_usage_unavailable" | "billing_usage_invalid" | "billing_usage_incomplete"
  | "billing_evidence_invalid" | "billing_evidence_incomplete" | "billing_hold_unavailable"
  | "billing_price_unavailable" | "billing_pricing_unavailable";

export interface TaskQueueSnapshot {
  phase: "waiting_capacity" | "waiting_channel" | "dispatch_pending" | "waiting_upstream";
  since: string;
  deadlineAt?: string;
  /** Upstream gateway queue position, present while waiting_upstream and observed. */
  queuePosition?: number;
  /** Upstream gateway queue ETA in seconds, present while waiting_upstream and observed. */
  etaSeconds?: number;
  cancelable: boolean;
}

export interface TaskTiming {
  submittedAt: string;
  providerSubmittedAt?: string;
  providerCompletedAt?: string;
  /** First public terminal response; background media may still be preparing. */
  firstResponseAt?: string;
  /** Background media ready response; absent while preparing or after persistence failure. */
  secondResponseAt?: string;
  storedAt?: string;
  persistPolicy?: "terminal" | "background";
  deliveryStatus?: "preparing" | "ready" | "failed";
  preparationMs: number | null;
  generationMs: number | null;
  /** Background: elapsed persistence stage until assets are ready; terminal: measured transfer. Null when unfinished or unavailable. */
  storageMs: number | null;
  /** First-response time outside measured preparation/generation and any pre-response storage; not a separately instrumented stage. */
  responseOverheadMs?: number | null;
  /** Start of in-progress background persistence, when a worker has claimed the output. */
  storageStartedAt?: string;
  /** Background persistence elapsed time from first response to durable asset readiness. */
  persistMs?: number | null;
  totalMs: number | null;
  /** Incident-only estimate of service readiness; actual public response remains in totalMs. */
  correction?: {
    incidentId: string;
    outputReadyAt: string;
    serviceReadyMs: number;
    deploymentDelayMs: number;
  };
  startSource: "client" | "server";
  /** Server clock reading taken when this snapshot was built; lets clients anchor live timers to server time. */
  observedAt?: string;
}

/** Repository/console read model. HTTP clients receive TaskReceipt or TaskResult. */
export interface Task<TOutput = JsonValue> extends PublicErrorFields {
  id: string;
  /** Original platform request ID, shared with request history. */
  requestId?: string;
  /** Public Run ID, when this Task belongs to a shared-input run. */
  runId?: string;
  model: string;
  operation: string;
  schemaVersion: number;
  /** Model request parameters; native-managed tasks retain the upstream field structure. */
  input?: JsonObject;
  status: TaskStatus;
  /** Present only while an asynchronous Task is queued. */
  queue?: TaskQueueSnapshot;
  /** Persistence policy frozen when the Task is created. Background is limited to video Tasks. */
  persistPolicy?: "terminal" | "background";
  /**
   * Regional media availability. "preparing" means the task result is delivered
   * with provider media URLs while Nexra persists them to its own object storage
   * in the background; "ready" means persisted URLs; "failed" means background
   * persistence exhausted its retries and the provider URLs have expired.
   * Execution/billing remain terminal regardless of this value.
   */
  deliveryStatus?: "preparing" | "ready" | "failed";
  progress?: TaskProgress;
  /** Native-managed tasks preserve the final response shape and media URLs; catalog models may normalize model identifiers. */
  output?: TOutput;
  assets: MediaAsset[];
  error?: PublicError;
  usage?: TaskUsage;
  cost?: TaskCost;
  /** Customer billing state; absent on older servers. */
  billingStatus?: "pending" | "succeeded" | "failed" | "refunded" | "unavailable";
  /** Safe read-only cause; execution failures release authorization rather than failing settlement. */
  billingReason?: TaskBillingReason;
  /** Final customer sale settlement. Provider procurement cost is never exposed here. */
  settlement?: NexraSettlement;
  /**
   * Caller-supplied business data, returned unchanged; never sent to the provider.
   * The platform extracts the reserved string keys `callerUrl` (caller origin,
   * https URL), `endUserId` (opaque end-user identifier), and `endUserIp`
   * (IPv4/IPv6 address) for support diagnostics. These caller assertions are
   * checked for format only and remain unchanged inside `metadata`.
   */
  metadata?: JsonObject;
  timing?: TaskTiming;
  /** Server-side Task record creation time (ISO 8601 UTC). */
  createdAt: string;
  /** Latest Task record update, not the generation completion time (ISO 8601 UTC). */
  updatedAt: string;
  /** First worker execution start; absent while queued or cancelled before execution. */
  startedAt?: string;
  /**
   * Terminal succeeded/failed/cancelled time. Synchronously persisted tasks
   * (default) reach succeeded only after required asset storage; background-
   * persisted tasks are declared succeeded at result delivery while storage
   * continues, signalled by `deliveryStatus` and the task timing second response.
   */
  completedAt?: string;
  /** @deprecated Reserved; no Task expiry policy is currently enforced. Not a media URL expiry. */
  expiresAt?: string;
}

/** POST /tasks returns acceptance only, including for idempotent replays. */
export type TaskReceipt = Pick<Task, "id" | "status"> & PublicErrorContext;

/** Public Task result; platform diagnostics and business tags live in RequestDetails. */
export type TaskResult<TOutput = JsonValue> = Pick<Task<TOutput>, "id" | "model" | "status"> &
  Partial<Pick<Task<TOutput>, "output" | "assets" | "persistPolicy" | "deliveryStatus" | "settlement" | "queue">> & PublicErrorFields;

/** Platform request details, used by both managed Tasks and native proxy requests. */
export interface RequestDetails extends PublicErrorFields {
  id: string;
  /** Owning Nexra account, used to separate users in system request history. */
  accountId?: string | null;
  nexraRequestId?: string;
  taskId: string | null;
  method: string;
  path: string;
  protocol?: string;
  accessMode?: string | null;
  model: string | null;
  operation: string | null;
  status: string;
  /** Linked Task lifecycle when this request accepted a Task; HTTP `status` stays accepted. */
  taskStatus?: string | null;
  persistPolicy?: "terminal" | "background";
  deliveryStatus?: Task["deliveryStatus"];
  errorCode: string | null;
  /** HTTP duration, never substituted with asynchronous task duration. */
  durationMs: number | null;
  timing?: TaskTiming;
  cost?: TaskCost;
  billingStatus?: Task["billingStatus"];
  billingReason?: Task["billingReason"];
  settlement?: NexraSettlement;
  metadata?: JsonObject;
  /** Caller-asserted origin recorded with the request; diagnostics display only. */
  callerUrl?: string | null;
  /** Client address recorded at the trusted HTTP ingress; absent on historical requests. */
  clientIp?: string | null;
  /** Caller-asserted end-user identifier recorded with the request; diagnostics display only. */
  endUserId?: string | null;
  endUserIp?: string | null;
  purpose?: string | null;
  sourceSessionId?: string | null;
  sourceTaskId?: string | null;
  /** Name of the API key that authenticated this request; diagnostics display only. */
  apiKeyName?: string | null;
  /** Latest known execution update; omitted when not observed. */
  updatedAt?: string;
  runId?: string;
  schemaVersion?: number;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  input: JsonObject | null;
  /** Present only on single-request reads (`includeOutput`). */
  output?: JsonValue | null;
  /** Output media with durable TOS URLs; present only on `includeOutput`. */
  assets?: MediaAsset[];
  receivedAt: string;
  finishedAt: string | null;
}

export function taskReceipt(task: TaskReceipt & { requestId?: string }, context?: PublicErrorContext): TaskReceipt {
  const fields = pickPublicErrorFields(task, context);
  return { id: task.id, status: task.status,
    ...(fields.nexraRequestId ? { nexraRequestId: fields.nexraRequestId } : {}),
    ...(fields.nexraHttpStatus !== undefined ? { nexraHttpStatus: fields.nexraHttpStatus } : {}),
  };
}

export function taskResult<TOutput>(task: Task<TOutput>): TaskResult<TOutput> {
  return {
    id: task.id, model: task.model, status: task.status,
    ...(task.queue ? { queue: task.queue } : {}),
    ...(task.assets.length ? { assets: task.assets } : {}),
    ...pickPublicErrorFields(task),
    ...(task.output !== undefined ? { output: task.output } : {}),
    ...(task.persistPolicy ? { persistPolicy: task.persistPolicy } : {}),
    ...(task.deliveryStatus ? { deliveryStatus: task.deliveryStatus } : {}),
    ...(task.settlement ? { settlement: task.settlement } : {}),
  };
}

export type TaskEventType =
  | "state"
  | "progress"
  | "preview"
  | "log"
  | "usage"
  | "warning"
  | "result";

export interface TaskEvent {
  id: string;
  taskId: string;
  sequence: number;
  type: TaskEventType;
  status?: TaskStatus;
  progress?: TaskProgress;
  asset?: MediaAsset;
  message?: string;
  payload?: JsonObject;
  occurredAt: string;
}

/** @deprecated Use CreateTaskRequest.callback_url. Kept for existing callers. */
export type TaskWebhookConfig = JsonObject & Readonly<{
  url: string;
  /** Shared HMAC secret used to verify Nexra callback signatures. */
  secret: string;
  events?: Array<"completed">;
}>;

export const CREATE_TASK_CONTROL_FIELDS = [
  "model",
  "callback_url",
  "metadata",
  "quoteToken",
  "submittedAt",
  "schemaVersion",
  "webhook",
] as const;

export type CreateTaskControlField = (typeof CREATE_TASK_CONTROL_FIELDS)[number];

export type CreateTaskControls = Readonly<{
  /** @internal Console telemetry only: browser send time before uploads, not execution time. */
  submittedAt?: string;
  model: string;
  /** Signed Nexra quote used to lock this input to a sale-price revision. */
  quoteToken?: string;
  /** Optional business identifiers such as orderId, available in request details. Do not include secrets. */
  metadata?: JsonObject;
  /** Platform completion callback, independent of provider callback support. Never sent upstream. */
  callback_url?: string;
  /** @deprecated Use callback_url; do not supply both. */
  webhook?: TaskWebhookConfig;
  /** @deprecated Compatibility check only; the server resolves the current contract. */
  schemaVersion?: number;
}>;

/** Public Nexra Task request. Category parameters are top-level fields; `input`
 * is deliberately not an envelope. Persistent Task records still retain their
 * own `input` snapshot after the HTTP boundary separates control fields. */
export type CreateTaskRequest<TInput extends JsonObject = JsonObject> = TInput & CreateTaskControls;

/** Extract category parameters from the flat public Task request. */
export function taskRequestInput(request: CreateTaskRequest): JsonObject {
  if (Object.hasOwn(request, "input")) {
    throw new Error("Task parameters must be top-level; remove the input wrapper");
  }
  const controls = new Set<string>(CREATE_TASK_CONTROL_FIELDS);
  return Object.fromEntries(Object.entries(request).filter(([key]) => !controls.has(key))) as JsonObject;
}

/** Build a flat Task request and reject category fields reserved by Task control. */
export function createTaskRequest<TInput extends JsonObject>(
  model: string,
  input: TInput,
  controls: Omit<CreateTaskControls, "model"> = {},
): CreateTaskRequest<TInput> {
  for (const field of [...CREATE_TASK_CONTROL_FIELDS, "input"] as const) {
    if (Object.hasOwn(input, field)) {
      throw new Error(`Nexra model parameters cannot use reserved Task field: ${field}`);
    }
  }
  return { model, ...input, ...controls } as CreateTaskRequest<TInput>;
}

/** One user input fanned out to independent durable model Tasks. */
export interface TaskRun<TOutput = JsonValue> {
  id: string;
  inputMode: string;
  input: JsonObject;
  models: string[];
  repetitions: number;
  tasks: Task<TOutput>[];
  metadata?: JsonObject;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskRunRequest<TInput = Record<string, unknown>> {
  /** Versioned experience-center canonical input. Omitted means native shared input. */
  inputFormat?: "sandbox-v1";
  submittedAt?: string;
  models: string[];
  repetitions?: number;
  inputMode: string;
  input: TInput;
  metadata?: JsonObject;
}

export interface TaskRunListParams {
  status?: TaskStatus;
  model?: string;
  limit?: number;
  cursor?: string;
}

export interface TaskListParams extends RequestHistoryFilters {
  status?: TaskStatus;
  model?: string;
  limit?: number;
  cursor?: string;
  orphanOnly?: boolean;
}

export type TaskPage<TOutput = JsonValue> = CursorPage<Task<TOutput>>;

export interface RunResult<TOutput = JsonValue> {
  taskId: string;
  output: TOutput;
  assets: MediaAsset[];
  usage?: TaskUsage;
  cost?: TaskCost;
  task: TaskResult<TOutput>;
}

export function isTerminalTaskStatus(status: TaskStatus): status is TerminalTaskStatus {
  return status === "succeeded" || status === "failed" || status === "cancelled";
}
